import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ActivityIcon, BookOpenIcon, DownloadIcon, LayoutListIcon, MoreVerticalIcon, PlayIcon, SearchIcon, UserPlusIcon, UserIcon, UsersIcon, CheckCircle2Icon, StarIcon, RepeatIcon, type LucideIcon } from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Button } from "@component/button";
import BreadcrumbNav from "@component/breadcrumb-nav";
import { Card, CardContent } from "@component/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { ActivityLog } from "@component/training/activity-log";
import { AddEmployeesDialog } from "@component/training/add-employees-dialog";
import { EmployeeProfileDrawer } from "@component/training/employee-profile-drawer";
import { TrainingStatusPill } from "@component/training/status-pill";
import { TestAsEmployeeDialog } from "@component/training/test-as-employee-dialog";
import {
    TrainingErrorCard,
    TrainingSkeletonRows,
} from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import { RubricEditor } from "@container/training/rubric-editor";
import { CampaignKnowledgePanel } from "@component/training/campaign-knowledge-panel";
import { KB_SCOPES_ENABLED } from "@service/knowledge-base";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import {
    createTestSession,
    deriveActivityLog,
    exportCampaignCSV,
    getCampaign,
    getCampaignInvitations,
    getCampaignStats,
    getEmployeeActivityLog,
    getEmployeeAttempts,
    pickPersonaName,
    resendInvitation,
    updateCampaignStatus,
} from "@service/training";
import type { ActivityEvent, CampaignStats, InvitationRow } from "@service/training";
import type { CampaignStatus } from "@type/training";
import type { TrainingCampaignDetail, TrainingSession } from "@type/training";
import { TRAINING_TYPE_LABEL, formatSessionStatus } from "@type/training";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";

// Status dot colors, shared between the status dropdown items and the trigger.
const STATUS_DOT: Record<CampaignStatus, string> = {
    draft: "bg-gray-400",
    active: "bg-emerald-500",
    archived: "bg-amber-400",
};

export default function TrainingCampaignDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [, currentUser] = useUserProfileContext();
    const [campaign, setCampaign] = useState<TrainingCampaignDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [selected, setSelected] = useState<TrainingSession | null>(null);
    // Employee Profile drawer detail — fetched by email (attempts + activity log).
    const [selectedAttempts, setSelectedAttempts] = useState<TrainingSession[]>([]);
    const [selectedTimeline, setSelectedTimeline] = useState<ActivityEvent[]>([]);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const drawerReqRef = useRef(0);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [addEmployeesOpen, setAddEmployeesOpen] = useState(false);
    const [addToast, setAddToast] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"employees" | "rubric" | "activity" | "knowledge">("employees");
    const [invitations, setInvitations] = useState<InvitationRow[]>([]);
    const [stats, setStats] = useState<CampaignStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [resending, setResending] = useState<string | null>(null);
    // Pre-fill the employee search from the URL (?search=<email>) so deep links
    // from the dashboard Team insights land on the campaign with the employee
    // already filtered.
    const [employeeSearch, setEmployeeSearch] = useState(
        () => new URLSearchParams(window.location.search).get("search") ?? "",
    );
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState("all");

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getCampaign(id)
            .then((c) => {
                if (cancelled) return;
                setCampaign(c);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[training] campaign detail load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, reloadTick]);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        getCampaignInvitations(id)
            .then((rows) => { if (!cancelled) setInvitations(rows); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [id, reloadTick]);

    // Header card metrics come from the lightweight stats endpoint. The cards
    // render a skeleton while this is in flight.
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setStatsLoading(true);
        getCampaignStats(id)
            .then((s) => { if (!cancelled) setStats(s); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setStatsLoading(false); });
        return () => { cancelled = true; };
    }, [id, reloadTick]);

    const autoOpenDone = useRef(false);
    useEffect(() => {
        if (!campaign || autoOpenDone.current) return;
        // Deep-link to an employee's profile drawer by EMAIL only — a stable key.
        // (Name matching was rejected in review: two trainees with the same
        // display name in one campaign would open the wrong person's data. Recent
        // activity will deep-link here once the BE adds employee_email to its
        // activity items — see be-handoff-dashboard-recent-activity.md.)
        const email = new URLSearchParams(window.location.search).get("openEmployee");
        if (!email) return;
        autoOpenDone.current = true;
        const decodedEmail = decodeURIComponent(email);
        const employeeSessions = campaign.sessions.filter(
            (s) => !s.is_test && s.employee_email === decodedEmail,
        );
        if (!employeeSessions.length) return;
        const latest = employeeSessions.reduce((best, s) =>
            s.attempt_number > best.attempt_number ? s : best,
        );
        setSelected(latest);
    }, [campaign]);

    // When an employee is selected, fetch their attempt history + activity log
    // by email (same as the Reports → Employees drawer), with a skeleton.
    useEffect(() => {
        if (!selected) return;
        const email = selected.employee_email;
        const reqId = ++drawerReqRef.current;
        setSelectedAttempts([]);
        setSelectedTimeline([]);
        setDrawerLoading(true);
        Promise.all([
            getEmployeeAttempts(email, id),
            getEmployeeActivityLog(email, id),
        ])
            .then(([attempts, timeline]) => {
                if (drawerReqRef.current !== reqId) return;
                setSelectedAttempts(attempts);
                setSelectedTimeline(timeline);
            })
            .catch((err: unknown) => {
                catchErrorLog(
                    `[training] employee detail load failed: ${
                        err instanceof Error ? err.message : String(err)
                    }`,
                );
                if (drawerReqRef.current !== reqId) return;
                setSelectedAttempts([]);
                setSelectedTimeline([]);
            })
            .finally(() => {
                if (drawerReqRef.current === reqId) setDrawerLoading(false);
            });
    }, [selected, id]);

    const canWrite = currentUser?.permission !== "member";
    // Trial/usage gate for starting a roleplay (trainer "test as employee").
    const { isTrainingRoleplayBlocked } = useSubscription();
    const roleplayBlockedTitle = isTrainingRoleplayBlocked
        ? "Your trial has expired or your roleplay-session limit is reached. Upgrade to run roleplays."
        : undefined;
    const isOwner = currentUser?.permission === "owner";

    const hrName = currentUser
        ? [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" ") || currentUser.username
        : undefined;

    const activityEvents = useMemo(
        () => (campaign ? deriveActivityLog(campaign, hrName) : []),
        [campaign, hrName]
    );

    if (error) {
        return (
            <TrainingAuthLayout>
                <div className="max-w-4xl mx-auto">
                    <TrainingErrorCard
                        title="We couldn't load this campaign."
                        onRetry={() => setReloadTick((t) => t + 1)}
                    />
                </div>
            </TrainingAuthLayout>
        );
    }

    if (loading) {
        return (
            <TrainingAuthLayout>
                <div className="max-w-4xl mx-auto">
                    <Card>
                        <CardContent className="p-5">
                            <TrainingSkeletonRows rows={6} />
                        </CardContent>
                    </Card>
                </div>
            </TrainingAuthLayout>
        );
    }

    if (!campaign) {
        return (
            <TrainingAuthLayout>
                <div className="max-w-4xl mx-auto">
                    <div className="text-sm text-gray-600">Campaign not found.</div>
                    <Button className="mt-4" onClick={() => navigate("/training/campaigns")}>
                        Back to campaigns
                    </Button>
                </div>
            </TrainingAuthLayout>
        );
    }

    const latestByEmployee = new Map<string, TrainingSession>();
    for (const s of campaign.sessions) {
        if (s.is_test) continue;
        const existing = latestByEmployee.get(s.employee_email);
        if (!existing || s.attempt_number > existing.attempt_number) {
            latestByEmployee.set(s.employee_email, s);
        }
    }
    const allRows = Array.from(latestByEmployee.values());
    const invMap = new Map(invitations.map((r) => [r.employee_email, r]));

    const empStatusCounts: Record<string, number> = { all: allRows.length };
    for (const s of allRows) {
        const st = invMap.get(s.employee_email)?.last_status ?? null;
        if (st) empStatusCounts[st] = (empStatusCounts[st] ?? 0) + 1;
    }

    const rows = allRows.filter((s) => {
        if (employeeSearch) {
            const q = employeeSearch.toLowerCase();
            if (
                !(s.employee_name ?? "").toLowerCase().includes(q) &&
                !s.employee_email.toLowerCase().includes(q)
            ) return false;
        }
        if (employeeStatusFilter !== "all") {
            const st = invMap.get(s.employee_email)?.last_status ?? null;
            if (st !== employeeStatusFilter) return false;
        }
        return true;
    });
    const personaName = campaign.persona_name ?? pickPersonaName(campaign.persona_id);

    // Header card metrics come straight from the stats endpoint — no local
    // fallback. Cards show "—" until stats resolves (or if the value is null).
    const totalEmployees = stats?.total_employees ?? null;
    const totalCompleted = stats?.total_completed ?? null;
    const avgScore = stats?.avg_score ?? null;
    const totalAttempts = stats?.total_attempts ?? null;

    const handleTestCampaign = async () => {
        if (!id) return;
        try {
            const token = await createTestSession(id);
            if (!token) {
                throw new Error(
                    "We couldn't start your test session. Please try again."
                );
            }
            window.open(`/training-session/${token}`, "_blank", "noopener");
        } catch (err) {
            catchErrorLog(
                `[training] test session mint failed: ${
                    err instanceof Error ? err.message : String(err)
                }`
            );
            throw err;
        }
    };

    const handleStatusChange = async (next: CampaignStatus) => {
        if (!id || !campaign || campaign.status === next) return;
        const updated = await updateCampaignStatus(id, next);
        if (updated) {
            setCampaign({ ...campaign, status: updated.status });
        }
    };

    const handleResend = async (email: string) => {
        if (!id || resending) return;
        setResending(email);
        try {
            await resendInvitation(id, email);
            setReloadTick((n) => n + 1);
        } catch {
            // non-critical
        } finally {
            setResending(null);
        }
    };

    return (
        <TrainingAuthLayout>
            <div>
                <div className="mb-6">
                    <div className="mb-3">
                        <BreadcrumbNav
                            items={[
                                { label: "Campaigns", href: "/training/campaigns" },
                                { label: campaign.name },
                            ]}
                        />
                    </div>
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">{campaign.name}</h1>
                            <div className="text-sm text-gray-600 mt-1">
                                {TRAINING_TYPE_LABEL[campaign.training_type]} · {personaName}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select
                                value={campaign.status}
                                onValueChange={(v) => handleStatusChange(v as CampaignStatus)}
                                disabled={!canWrite}
                            >
                                <SelectTrigger className="w-[130px] text-sm capitalize">
                                    {/* !flex overrides the trigger's [&>span]:line-clamp-1
                                        (display:-webkit-box) which would otherwise clip the dot. */}
                                    <span className="!flex items-center gap-2 overflow-visible">
                                        <SelectValue />
                                    </span>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">
                                        <span className="flex items-center gap-2">
                                            <span className={cn("h-2 w-2 rounded-full shrink-0", STATUS_DOT.draft)} />
                                            Draft
                                        </span>
                                    </SelectItem>
                                    <SelectItem value="active">
                                        <span className="flex items-center gap-2">
                                            <span className={cn("h-2 w-2 rounded-full shrink-0", STATUS_DOT.active)} />
                                            Active
                                        </span>
                                    </SelectItem>
                                    {isOwner && (
                                        <SelectItem value="archived">
                                            <span className="flex items-center gap-2">
                                                <span className={cn("h-2 w-2 rounded-full shrink-0", STATUS_DOT.archived)} />
                                                Archived
                                            </span>
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                            {/* Publish: prominent CTA to launch a draft campaign
                                (flips to active + sends invitations on the backend). */}
                            {canWrite && campaign.status === "draft" && (
                                <Button onClick={() => handleStatusChange("active")}>
                                    <PlayIcon className="w-4 h-4" />
                                    Publish
                                </Button>
                            )}
                            {/* Archived is read-only — no adding employees. */}
                            {canWrite && campaign.status !== "archived" && (
                                <Button
                                    variant="outline"
                                    onClick={() => setAddEmployeesOpen(true)}
                                >
                                    <UserPlusIcon className="w-4 h-4" />
                                    Add employees
                                </Button>
                            )}
                            {/* Retired campaigns can't be test-driven. */}
                            {canWrite && campaign.status !== "archived" && (
                                <Button
                                    variant="outline"
                                    onClick={() => setTestDialogOpen(true)}
                                    disabled={isTrainingRoleplayBlocked}
                                    title={roleplayBlockedTitle}
                                >
                                    <PlayIcon className="w-4 h-4" />
                                    Test as employee
                                </Button>
                            )}
                            <TestAsEmployeeDialog
                                open={testDialogOpen}
                                onOpenChange={setTestDialogOpen}
                                onConfirm={handleTestCampaign}
                            />
                            {id && (
                                <AddEmployeesDialog
                                    open={addEmployeesOpen}
                                    onOpenChange={setAddEmployeesOpen}
                                    campaignId={id}
                                    onAdded={(added, skipped) => {
                                        const parts = [
                                            `${added} added`,
                                        ];
                                        if (skipped > 0) {
                                            parts.push(
                                                `${skipped} skipped (already invited)`
                                            );
                                        }
                                        setAddToast(parts.join(", "));
                                        setReloadTick((n) => n + 1);
                                        window.setTimeout(
                                            () => setAddToast(null),
                                            4000
                                        );
                                    }}
                                />
                            )}
                            <Button variant="outline" onClick={() => exportCampaignCSV(campaign)}>
                                <DownloadIcon className="w-4 h-4" />
                                Export CSV
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-6">
                    <StatTile
                        label="Employees"
                        value={totalEmployees ?? "—"}
                        loading={statsLoading}
                        icon={UsersIcon}
                        tooltip="Total employees enrolled in this campaign."
                    />
                    <StatTile
                        label="Completed"
                        value={
                            totalCompleted !== null && totalEmployees !== null
                                ? `${totalCompleted}/${totalEmployees}`
                                : "—"
                        }
                        loading={statsLoading}
                        icon={CheckCircle2Icon}
                        tooltip="Employees who completed at least one attempt, out of total enrolled."
                    />
                    <StatTile
                        label="Avg Score"
                        value={avgScore !== null ? `${Math.round(avgScore)}/100` : "—"}
                        loading={statsLoading}
                        icon={StarIcon}
                        tooltip="Average score across all completed attempts (out of 100)."
                    />
                    <StatTile
                        label="Total Attempts"
                        value={totalAttempts ?? "—"}
                        loading={statsLoading}
                        icon={RepeatIcon}
                        tooltip="Total roleplay attempts submitted across all employees."
                    />
                </div>

                {addToast && (
                    <div className="mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                        {addToast}
                    </div>
                )}

                <div className="flex gap-1 mb-4 border-b border-gray-200">
                    {([
                        "employees",
                        "activity",
                        "rubric",
                        ...(KB_SCOPES_ENABLED ? (["knowledge"] as const) : []),
                    ] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={cn(
                                "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                                activeTab === tab
                                    ? "border-blue-600 text-blue-700"
                                    : "border-transparent text-gray-500 hover:text-gray-700"
                            )}
                        >
                            {tab === "employees" ? (
                                <><UsersIcon className="w-3.5 h-3.5" />Employees</>
                            ) : tab === "rubric" ? (
                                <><LayoutListIcon className="w-3.5 h-3.5" />Scoring Rubric</>
                            ) : tab === "knowledge" ? (
                                <><BookOpenIcon className="w-3.5 h-3.5" />Knowledge</>
                            ) : (
                                <><ActivityIcon className="w-3.5 h-3.5" />Campaign Activity</>
                            )}
                        </button>
                    ))}
                </div>

                {activeTab === "rubric" && id && (
                    <div className="mb-6">
                        <RubricEditor
                            campaignId={id}
                            trainingType={campaign.training_type}
                            // Draft campaigns get the editable weights/customise
                            // view; once Active the rubric is locked, so fall
                            // through to the read-only side-by-side layout with
                            // colour-coded High/Mid/Low chips.
                            showOnlyWeights={campaign.status === "draft"}
                            readOnly={campaign.status !== "draft"}
                            onSave={() => {
                                setAddToast("Campaign scoring rubric updated successfully.");
                                window.setTimeout(() => setAddToast(null), 4000);
                            }}
                        />
                    </div>
                )}

                {activeTab === "employees" && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="relative flex-1">
                                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search employees..."
                                    value={employeeSearch}
                                    onChange={(e) => setEmployeeSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <Select value={employeeStatusFilter} onValueChange={setEmployeeStatusFilter}>
                                <SelectTrigger className="w-[180px] text-sm flex-shrink-0">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {(["all", "completed", "started", "in_progress", "invited", "scoring", "failed", "expired", "abandoned"] as const).map((key) => {
                                        const count = empStatusCounts[key] ?? 0;
                                        if (key !== "all" && count === 0) return null;
                                        return (
                                            <SelectItem key={key} value={key}>
                                                {key === "all" ? "All Statuses" : formatSessionStatus(key)}{" "}
                                                <span className="text-gray-400">({count})</span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        {allRows.length === 0 ? (
                            <Card>
                                <div className="px-6 py-12 text-center">
                                    <PlayIcon className="w-8 h-8 mx-auto text-gray-400" />
                                    <h3 className="mt-3 text-base font-semibold text-gray-900">
                                        No employees have started yet.
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-600 max-w-md mx-auto">
                                        Try the roleplay yourself first to see exactly
                                        what your team will experience before you send
                                        invites.
                                    </p>
                                    <div className="mt-4 flex items-center justify-center gap-2">
                                        {canWrite && (
                                            <Button
                                                onClick={() => setTestDialogOpen(true)}
                                                disabled={isTrainingRoleplayBlocked}
                                                title={roleplayBlockedTitle}
                                            >
                                                <PlayIcon className="w-4 h-4" />
                                                Test as employee
                                            </Button>
                                        )}
                                        {canWrite && (
                                            <Button
                                                variant="outline"
                                                onClick={() => setAddEmployeesOpen(true)}
                                            >
                                                <UserPlusIcon className="w-4 h-4" />
                                                Add employees
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ) : rows.length === 0 ? (
                            <Card>
                                <div className="px-6 py-10 text-center text-sm text-gray-500 italic">
                                    No employees match your search.
                                </div>
                            </Card>
                        ) : (
                            <Card className="overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                                            <tr className="h-[45px]">
                                                <th className="text-left px-4 py-4 whitespace-nowrap font-medium">Name</th>
                                                <th className="text-center px-4 py-4 whitespace-nowrap font-medium">Role · Dept</th>
                                                <th className="text-left px-4 py-4 whitespace-nowrap font-medium">Email</th>
                                                <th className="text-center px-4 py-4 whitespace-nowrap font-medium">Status</th>
                                                <th className="text-center px-4 py-4 whitespace-nowrap font-medium">Attempts</th>
                                                <th className="text-center px-4 py-4 whitespace-nowrap font-medium">Latest Score</th>
                                                <th className="px-4 py-4" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {rows.map((s) => {
                                                const attemptsForEmployee = campaign.sessions.filter(
                                                    (x) => x.employee_email === s.employee_email && !x.is_test && x.started_at != null
                                                ).length;
                                                const inv = invMap.get(s.employee_email);
                                                const canResend = inv && ["queued", "sent", "opened", "clicked", "bounced"].includes(inv.status);
                                                const latestScore = inv?.latest_score ?? null;
                                                const latestScoredSession = campaign.sessions
                                                    .filter(
                                                        (x) =>
                                                            x.employee_email === s.employee_email &&
                                                            !x.is_test &&
                                                            x.status === "completed" &&
                                                            x.total_score !== null,
                                                    )
                                                    .reduce<TrainingSession | null>(
                                                        (best, x) =>
                                                            !best || x.attempt_number > best.attempt_number
                                                                ? x
                                                                : best,
                                                        null,
                                                    );
                                                const scoreResultsToken =
                                                    latestScoredSession?.token ?? s.token;
                                                return (
                                                    <tr
                                                        key={s.id}
                                                        onClick={() => setSelected(s)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap">
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                                    <UserIcon className="w-4 h-4 text-blue-600" />
                                                                </div>
                                                                <span>{s.employee_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap text-center">
                                                            {s.employee_role || (s.employee_department ? null : <span className="text-gray-400">—</span>)}
                                                            {s.employee_role && s.employee_department ? (
                                                                <span className="text-gray-400"> • </span>
                                                            ) : null}
                                                            {s.employee_department ?? null}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <span className="text-sm text-gray-600">{s.employee_email}</span>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                                            {inv?.last_status ? (
                                                                <TrainingStatusPill status={inv.last_status} />
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-700 text-center whitespace-nowrap">
                                                            {attemptsForEmployee === 0
                                                                ? <span className="text-gray-400">—</span>
                                                                : attemptsForEmployee
                                                            }
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-center whitespace-nowrap">
                                                            {latestScore !== null ? (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/training-results/${scoreResultsToken}?mode=employer`);
                                                                    }}
                                                                    className="text-gray-900 font-medium hover:underline"
                                                                >
                                                                    {latestScore}/100
                                                                </button>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                                                                        <MoreVerticalIcon className="w-4 h-4" />
                                                                    </button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    {canWrite && canResend && (
                                                                        <DropdownMenuItem
                                                                            onSelect={() => handleResend(s.employee_email)}
                                                                            disabled={resending === s.employee_email}
                                                                        >
                                                                            {resending === s.employee_email ? "Sending..." : "Resend invitation"}
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    <DropdownMenuItem
                                                                        onSelect={() => {
                                                                            const url = `${window.location.origin}/training-session/${s.token}`;
                                                                            navigator.clipboard.writeText(url);
                                                                        }}
                                                                    >
                                                                        Copy roleplay link
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem asChild>
                                                                        <a
                                                                            href={`/training-session/${s.token}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            Open roleplay link
                                                                        </a>
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}
                    </div>
                )}

                {activeTab === "activity" && (
                    <div className="mb-6">
                        <ActivityLog events={activityEvents} collapsible={false} />
                    </div>
                )}

                {activeTab === "knowledge" && id && (
                    <div className="mb-6">
                        <CampaignKnowledgePanel campaignId={id} canWrite={canWrite} />
                    </div>
                )}
            </div>

            {selected && (
                <EmployeeProfileDrawer
                    open={!!selected}
                    onClose={() => setSelected(null)}
                    name={selected.employee_name}
                    email={selected.employee_email}
                    role={selected.employee_role}
                    department={selected.employee_department}
                    personaName={personaName}
                    campaignName={campaign.name}
                    attempts={selectedAttempts}
                    timeline={selectedTimeline}
                    loading={drawerLoading}
                />
            )}
        </TrainingAuthLayout>
    );
}

function StatTile({
    label,
    value,
    loading = false,
    icon: Icon,
    tooltip,
}: {
    label: string;
    value: string | number;
    loading?: boolean;
    icon?: LucideIcon;
    tooltip?: string;
}) {
    const card = (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    {Icon && (
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50">
                            <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
                        {loading ? (
                            <div className="mt-2 h-7 w-16 bg-gray-100 rounded animate-pulse" />
                        ) : (
                            <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (tooltip) {
        return <Tooltip content={<p>{tooltip}</p>}>{card}</Tooltip>;
    }
    return card;
}
